import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

export default function ApiEnvironmentDoc({ onNavigate }) {
  return (
    <Box id="api-environment" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        API Environment
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        This screen shows a <strong>read-only</strong> view of environment variables as seen by the
        running <strong>Stack API</strong> server. Use it to confirm that staging or production has
        the right configuration (for example Auth0, database URL, feature flags) without shell access
        to the host—helpful for <strong>support</strong>, <strong>deploys</strong>, and{" "}
        <strong>on-call</strong> checks.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
        Why we have it
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>Verify config after a release</strong> — See whether expected variables are set
          and non-empty in the environment the API actually uses.
        </li>
        <li>
          <strong>Troubleshoot integrations</strong> — When Auth0, Circle, Squarespace, or the database
          misbehave, quickly check whether the relevant <code>*_DOMAIN</code>, <code>*_AUDIENCE</code>, or
          connection strings look present (not missing or obviously wrong).
        </li>
        <li>
          <strong>No secrets on screen</strong> — Variable names that include <code>SECRET</code> show
          a <strong>masked</strong> value (only the last few characters hint that something is set).
          You still should not share screenshots of this page publicly.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        What you see (how the API builds the list)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        The API exposes <code>GET /admin/env</code> (admin-only). It lists process environment keys
        that are <strong>ALL CAPS</strong>, contain an <strong>underscore</strong>, and are not in a
        small hard-coded list of terminal/shell noise. Everything else is omitted from the main table
        and listed under <strong>EXCLUDED_ENVS</strong> so you know it was filtered, not missing from
        the server.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        This page is <strong>not</strong> for editing variables—changes are made in your deployment
        platform or <code>.env</code> for local dev, then restart the API.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Test Sentry (admin UI)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        The <strong>Test Sentry</strong> button runs a client-side check against Sentry from the admin
        app when <code>VITE_SENTRY_DSN</code> is configured. It helps confirm the browser can report to
        Sentry; it is separate from API env vars but useful during the same “is monitoring wired up?”
        pass.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} paragraph>
        <strong>Quick support line:</strong> “We use <strong>API Environment</strong> to confirm the
        backend has the right settings for Auth0 and integrations. Values with SECRET in the name are
        masked; we don’t change env vars from this screen—ops updates config and restarts the API.”
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/env")}
          >
            Open API Environment →
          </Link>
        </Box>
      )}
    </Box>
  );
}
